import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { CallQuotaStatusDto } from "../../applicants-service"

interface BulkAiCallDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void>
  selectedCount: number
  isSubmitting: boolean
  quota?: CallQuotaStatusDto
}

export function BulkAiCallDialog({
  open,
  onOpenChange,
  onConfirm,
  selectedCount,
  isSubmitting,
  quota,
}: BulkAiCallDialogProps) {
  const label =
    selectedCount === 1 ? "1 Candidate" : `${selectedCount} Candidates`

  const exceedsQuota = !!quota && selectedCount > quota.remaining

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-card">
        <DialogHeader>
          <DialogTitle>Call {label} with AI</DialogTitle>
          <DialogDescription>
            An AI voice agent will call each selected candidate for a short
            screening interview.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-2">
          {quota && (
            <p
              className={
                exceedsQuota
                  ? "text-sm text-destructive"
                  : "text-sm text-muted-foreground"
              }
            >
              This will use {selectedCount} of your {quota.remaining}{" "}
              remaining call{quota.remaining !== 1 ? "s" : ""}
              {quota.resetsAt
                ? ` (resets ${new Date(quota.resetsAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})`
                : ""}
              .
            </p>
          )}
          {exceedsQuota && (
            <p className="text-sm text-destructive">
              You don't have enough calls remaining for this batch. Deselect
              some candidates or wait for your limit to reset.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isSubmitting || exceedsQuota}
          >
            {isSubmitting ? "Submitting..." : `Call ${label}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
